// Per-surah offline downloader.
//
// The service worker (/public/service-worker.js) already caches:
//   • everyayah.com audio (Cache-First → AUDIO_CACHE)
//   • /api/quran/surah/{n} & /api/quran/wordbyword/{key} (SWR → QURAN_API_CACHE)
//
// This module just orchestrates the right `fetch` calls for a given surah so
// the SW populates its caches deliberately (rather than opportunistically),
// and tracks completion in localStorage for the green "Downloaded" badge.

import { RECITER_FOLDERS } from "@/data/reciters";

const STORAGE_KEY = "quran_downloaded_surahs"; // JSON array of {surah, reciter}

function readManifest() {
  if (typeof window === "undefined") return [];
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
  } catch {
    return [];
  }
}

function writeManifest(list) {
  if (typeof window !== "undefined") {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  }
}

export function isSurahDownloaded(surahNumber, reciter) {
  return readManifest().some(
    (e) => e.surah === surahNumber && (!reciter || e.reciter === reciter)
  );
}

export function getDownloadedSurahs() {
  return readManifest();
}

export function removeSurahDownload(surahNumber, reciter) {
  const next = readManifest().filter(
    (e) => !(e.surah === surahNumber && (!reciter || e.reciter === reciter))
  );
  writeManifest(next);
  // Best-effort: ask the SW caches to delete what we just untracked. We can't
  // selectively purge from a normal page so just leave the cached entries —
  // they'll get evicted when the cache name version bumps. The user-facing
  // state (the badge) is what actually matters for UX.
}

function audioUrl(reciterValue, surahNum, ayahNum) {
  const folder = RECITER_FOLDERS[reciterValue] || "Alafasy_128kbps";
  return `https://everyayah.com/data/${folder}/${String(surahNum).padStart(3, "0")}${String(ayahNum).padStart(3, "0")}.mp3`;
}

/**
 * Downloads a single surah for offline use.
 * Returns a Promise resolving to {ok, total, completed}.
 *
 * @param {object} opts
 * @param {number} opts.surahNumber
 * @param {string} opts.reciter   - reciter edition id (e.g. "ar.alafasy")
 * @param {string} opts.apiBase   - API base URL (REACT_APP_BACKEND_URL + "/api")
 * @param {function} opts.onProgress - (completed, total) => void
 * @param {function} opts.shouldAbort - () => boolean
 */
export async function downloadSurahForOffline({
  surahNumber,
  reciter,
  apiBase,
  onProgress,
  shouldAbort,
}) {
  // 1) Fetch the surah text (populates QURAN_API_CACHE) AND tells us ayah count.
  const surahRes = await fetch(`${apiBase}/quran/surah/${surahNumber}`);
  if (!surahRes.ok) throw new Error(`Surah fetch failed: HTTP ${surahRes.status}`);
  const surah = await surahRes.json();
  const ayahs = surah.ayahs || [];
  const total = ayahs.length;
  if (total === 0) throw new Error("Surah has no ayahs");

  let completed = 0;
  onProgress?.(0, total);

  // 2) Pre-fetch each ayah's audio. Limit concurrency to 6 so we don't
  //    hammer everyayah and mobile networks.
  const queue = [...ayahs];
  const workers = Array.from({ length: 6 }, async () => {
    while (queue.length > 0) {
      if (shouldAbort?.()) throw new Error("aborted");
      const a = queue.shift();
      if (!a) break;
      try {
        await fetch(audioUrl(reciter, surahNumber, a.numberInSurah), {
          mode: "cors",
          credentials: "omit",
        });
      } catch {
        // Skip individual failures — we still want partial offline
        // availability rather than aborting the whole download.
      }
      completed += 1;
      onProgress?.(completed, total);
    }
  });
  await Promise.all(workers);

  // 3) Mark as downloaded. (Word-by-word data lazy-loads per ayah on Reader
  //    open and lives in the same cache, so it's still usable offline once
  //    the user has opened the surah at least once.)
  const manifest = readManifest();
  const exists = manifest.some(
    (e) => e.surah === surahNumber && e.reciter === reciter
  );
  if (!exists) {
    manifest.push({
      surah: surahNumber,
      reciter,
      ayah_count: total,
      downloaded_at: new Date().toISOString(),
    });
    writeManifest(manifest);
  }

  return { ok: true, total, completed };
}
